import type { SATTestUserInterventionArmAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'

import {
  type AssociationDefinitions,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import SATTestInterventionArmQ from './SATTestInterventionArmQ'
import SATTestUserQ from './SATTestUserQ'
import SATUserQ from './SATUserQ'

export type ActiveLearningInterventionArm =
  | 'Metrics'
  | 'Reflection'
  | 'Control'
  | 'Metrics + Reflection'
const ACTIVE_LEARNING_GROUP = 'march-23-active-learning'

export type June2024FeedbackInterventionArm =
  | 'Control'
  | 'Tutor Feedback'
  | 'Tutor Feedback + Socially-Oriented Learner Feedback'
  | 'Tutor Feedback + Goal-Oriented Learner Feedback'
  | 'Tutor Feedback - Exclude'
  | 'Tutor Feedback - Exclude (Full)'
export const JUNE_2024_FEEDBACK_GROUP_SLUG = 'june-24-tutor-math-ai-feedback'
export const AUGUST_2024_RCT_ARM_NAME = 'August 2024 RCT Learners'

const SATTestUserInterventionArmQ = {
  ...makeQueryModelV2<SATTestUserInterventionArmAttributes>('sat_test_user_intervention_arm')([
    'id',
    'sat_test_intervention_arm_id',
    'sat_test_user_id',
    'created_at',
    'deleted_at',
    'updated_at',
  ]),

  associations: (): AssociationDefinitions => ({
    SATTestUser: {
      query: SATTestUserQ,
      foreignKey: SATTestUserQ.id,
      sourceKey: SATTestUserInterventionArmQ.sat_test_user_id,
    },
    SATTestInterventionArm: {
      query: SATTestInterventionArmQ,
      foreignKey: SATTestInterventionArmQ.id,
      sourceKey: SATTestUserInterventionArmQ.sat_test_intervention_arm_id,
    },
  }),

  where: (
    {
      id = null,
      satTestInterventionArmId = null,
      include = [],
      order = null,
      limit = null,
      paranoid = true,
    }: {
      id?: number | null | SQLFragment<number>
      satTestInterventionArmId?: number | null | SQLFragment<number>
      include?: IncludeAssociations
      order?: QueryOrder | null
      limit?: number | null
      paranoid?: boolean
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<SATTestUserInterventionArmAttributes> => SQL`
        FROM ${SATTestUserInterventionArmQ}
        ${includeAssociationsToSql(SATTestUserInterventionArmQ, include)}
        WHERE
            ${id != null ? SQL`${SATTestUserInterventionArmQ.id} = ${id} AND` : SQL``}
            ${
              satTestInterventionArmId != null
                ? SQL`${SATTestUserInterventionArmQ.sat_test_intervention_arm_id} = ${satTestInterventionArmId} AND`
                : SQL``
            }
            ${paranoid ? SQL`${SATTestUserInterventionArmQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryOrderToSQL(order)}
        ${queryLimitToSQL(limit)}
    `,

  getMarchSATActiveLearningInterventionArms: ({
    userId,
  }: {
    userId: number | SQLFragment<number>
  }): SQLFragment<ActiveLearningInterventionArm> =>
    SATTestUserInterventionArmQ.where({
      include: [
        {
          association: 'SATTestInterventionArm',
          required: true,
          where: SQL`${SATTestInterventionArmQ.group_slug} = ${ACTIVE_LEARNING_GROUP}`,
        },
        {
          association: 'SATTestUser',
          required: true,
          include: [
            {
              association: 'SATUser',
              required: true,
              where: SQL`
                  ${SATUserQ.type} = 'tutor'
                  AND ${SATUserQ.user_id} = ${userId}`,
            },
          ],
        },
      ],
      limit: 1,
    }),

  getJune2024SATFeedbackExperimentTutorArm: ({
    tutorUserId,
  }: {
    tutorUserId: number | SQLFragment<number>
  }): SQLFragment<SATTestUserInterventionArmAttributes> =>
    SATTestUserInterventionArmQ.where({
      include: [
        {
          association: 'SATTestInterventionArm',
          required: true,
          where: SQL`${SATTestInterventionArmQ.group_slug} = ${JUNE_2024_FEEDBACK_GROUP_SLUG}`,
        },
        {
          association: 'SATTestUser',
          required: true,
          include: [
            {
              association: 'SATUser',
              required: true,
              where: SQL`
                  ${SATUserQ.hasType('tutor')}
                  AND ${SATUserQ.user_id} = ${tutorUserId}`,
            },
          ],
        },
      ],
      limit: 1,
    }),
}

export default SATTestUserInterventionArmQ
